import React from "react";
import {
  Shield,
  ShieldCheck,
  ShieldX,
  CalendarClock,
  Bell,
  RotateCw,
  AlertTriangle,
  Lightbulb,
  Building2,
  Hash,
  CalendarDays,
  Sparkles,
} from "lucide-react";
import RiskBadge from "./RiskBadge";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}

function Meta({ Icon, label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={13} className="text-zinc-500" strokeWidth={1.75} />
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      </div>
      <p className="font-display text-base font-semibold tracking-tight text-zinc-950 break-words">{value}</p>
    </div>
  );
}

function Card({ title, Icon, accent, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-7 h-7 rounded-md grid place-items-center ${accent}`}>
          <Icon size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function InsuranceAnalysisView({ analysis: a }) {
  return (
    <>
      {/* Header meta strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-8 pt-6 border-t border-zinc-200">
        <Meta Icon={Building2} label="Insurer" value={a.insurer || "—"} testId="meta-insurer" />
        <Meta Icon={Hash} label="Policy #" value={a.policy_number || "—"} testId="meta-policy-number" />
        <Meta Icon={Shield} label="Policy type" value={a.policy_type || "—"} testId="meta-policy-type" />
        <Meta Icon={CalendarDays} label="Effective" value={fmtDate(a.effective_date)} testId="meta-effective-date" />
        <Meta Icon={CalendarClock} label="Expires" value={fmtDate(a.expiration_date)} testId="meta-expiration-date" />
      </div>

      <div className="mt-6 space-y-6">
        {/* Executive summary */}
        <section
          className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-7"
          data-testid="ins-summary-section"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={15} className="text-zinc-700" strokeWidth={1.75} />
            <h2 className="font-display font-semibold text-base tracking-tight">Executive summary</h2>
          </div>
          <p className="text-[15px] leading-relaxed text-zinc-700">{a.summary || "—"}</p>
        </section>

        {/* Coverage + Exclusion bottom-line summaries (the headline cards) */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card title="What IS covered" Icon={ShieldCheck} testId="ins-coverage-summary" accent="bg-emerald-50 text-emerald-700">
            <p className="text-sm leading-relaxed text-zinc-700">{a.coverage_summary || "No coverage summary extracted."}</p>
          </Card>
          <Card title="What is NOT covered" Icon={ShieldX} testId="ins-exclusion-summary" accent="bg-rose-50 text-rose-700">
            <p className="text-sm leading-relaxed text-zinc-700">{a.exclusion_summary || "No exclusion summary extracted."}</p>
          </Card>
        </div>

        {/* Coverage limits & deductibles */}
        <Card title="Coverage limits" Icon={Shield} testId="ins-coverage-limits" accent="bg-indigo-50 text-indigo-700">
          {a.coverage_limits?.length ? (
            <ul className="divide-y divide-zinc-100" data-testid="ins-coverage-limits-list">
              {a.coverage_limits.map((c, i) => (
                <li key={i} className="flex items-baseline justify-between py-2.5 gap-4">
                  <span className="text-sm text-zinc-700">{c.label}</span>
                  <span className="font-display font-semibold text-sm text-zinc-950 tabular-nums">{c.amount}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-zinc-500">No coverage limits extracted.</p>
          )}
        </Card>

        <Card title="Deductibles" Icon={Shield} testId="ins-deductibles" accent="bg-amber-50 text-amber-700">
          {a.deductibles?.length ? (
            <ul className="divide-y divide-zinc-100" data-testid="ins-deductibles-list">
              {a.deductibles.map((d, i) => (
                <li key={i} className="flex items-baseline justify-between py-2.5 gap-4">
                  <span className="text-sm text-zinc-700">{d.label}</span>
                  <span className="font-display font-semibold text-sm text-zinc-950 tabular-nums">{d.amount}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-zinc-500">No deductibles extracted.</p>
          )}
        </Card>

        {/* Exclusions list (verbose) */}
        <Card title="Exclusions" Icon={ShieldX} testId="ins-exclusions-list" accent="bg-rose-50 text-rose-700">
          {a.exclusions?.length ? (
            <ul className="space-y-3">
              {a.exclusions.map((ex, i) => (
                <li key={i} data-testid={`ins-exclusion-${i}`} className="flex gap-2.5 text-sm text-zinc-700 leading-relaxed">
                  <span className="mt-2 w-1 h-1 rounded-full bg-rose-400 shrink-0" />
                  <span>{ex}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-zinc-500">No exclusions extracted.</p>
          )}
        </Card>

        {/* Notification deadlines */}
        <Card title="Notification deadlines" Icon={Bell} testId="ins-notification-deadlines" accent="bg-amber-50 text-amber-700">
          {a.notification_deadlines?.length ? (
            <ul className="divide-y divide-zinc-100">
              {a.notification_deadlines.map((n, i) => (
                <li key={i} className="py-2.5" data-testid={`ins-notification-${i}`}>
                  <p className="text-sm text-zinc-900 font-medium">{n.what}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{n.deadline}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-zinc-500">No notification deadlines extracted.</p>
          )}
        </Card>

        {/* Renewal requirements */}
        <Card title="Renewal requirements" Icon={RotateCw} testId="ins-renewal-requirements" accent="bg-emerald-50 text-emerald-700">
          {a.renewal_requirements?.length ? (
            <ul className="space-y-3">
              {a.renewal_requirements.map((r, i) => (
                <li key={i} data-testid={`ins-renewal-req-${i}`} className="flex gap-2.5 text-sm text-zinc-700 leading-relaxed">
                  <span className="mt-2 w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-zinc-500">No renewal requirements extracted.</p>
          )}
          {a.renewal_date && (
            <div className="mt-5 px-3.5 py-2.5 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center gap-2.5" data-testid="ins-renewal-date-banner">
              <CalendarClock size={15} className="text-zinc-700 shrink-0" />
              <p className="text-xs text-zinc-700">
                Earliest renewal notification deadline:{" "}
                <span className="font-semibold text-zinc-950">{fmtDate(a.renewal_date)}</span>
              </p>
            </div>
          )}
        </Card>

        {/* Risks (shared shape with lease) */}
        <Card title="Detected risks" Icon={AlertTriangle} testId="ins-risks-section" accent="bg-red-50 text-red-700">
          <ul className="space-y-3">
            {(a.detected_risks || []).map((r, i) => (
              <li key={i} data-testid={`ins-risk-${i}`} className="flex items-start gap-3 text-sm leading-relaxed">
                <AlertTriangle
                  size={15}
                  strokeWidth={1.75}
                  className={`shrink-0 mt-0.5 ${
                    r.level === "High" ? "text-red-500" : r.level === "Medium" ? "text-amber-500" : "text-emerald-500"
                  }`}
                />
                <div className="min-w-0">
                  <p className="text-zinc-800">{r.text}</p>
                  <RiskBadge level={r.level} size="sm" />
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {/* Recommended actions */}
        <Card title="Recommended actions" Icon={Lightbulb} testId="ins-actions-section" accent="bg-amber-50 text-amber-700">
          <ul className="space-y-3">
            {(a.recommended_actions || []).map((act, i) => (
              <li key={i} data-testid={`ins-action-${i}`} className="flex gap-3 text-sm text-zinc-700 leading-relaxed">
                <span className="font-display font-semibold text-zinc-400 shrink-0 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span>{act}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}
